
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type FontSize = 'normal' | 'large' | 'extra-large';
export type AudioSpeed = 0.5 | 0.75 | 1 | 1.25 | 1.5;

interface AccessibilitySettings {
  fontSize: FontSize;
  highContrast: boolean;
  audioSpeed: AudioSpeed;
  audioVolume: number;
  autoPlayAudio: boolean;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateFontSize: (size: FontSize) => void;
  toggleHighContrast: () => void;
  updateAudioSpeed: (speed: AudioSpeed) => void;
  updateAudioVolume: (volume: number) => void;
  toggleAutoPlayAudio: () => void;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 'normal',
  highContrast: false,
  audioSpeed: 1,
  audioVolume: 0.8,
  autoPlayAudio: false,
};

const AccessibilityContext = createContext<AccessibilityContextType>({
  settings: defaultSettings,
  updateFontSize: () => {},
  toggleHighContrast: () => {},
  updateAudioSpeed: () => {},
  updateAudioVolume: () => {},
  toggleAutoPlayAudio: () => {},
});

export const useAccessibility = () => useContext(AccessibilityContext);

interface AccessibilityProviderProps {
  children: ReactNode;
}

export const AccessibilityProvider = ({ children }: AccessibilityProviderProps) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibility-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...defaultSettings, ...parsed });
      } catch (error) {
        console.error('Error parsing accessibility settings:', error);
      }
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    applyAccessibilitySettings(settings);
  }, [settings]);

  const applyAccessibilitySettings = (settings: AccessibilitySettings) => {
    const root = document.documentElement;
    
    // Apply font size
    const fontSizeMultipliers = {
      normal: '1',
      large: '1.125',
      'extra-large': '1.25'
    };
    root.style.setProperty('--font-size-multiplier', fontSizeMultipliers[settings.fontSize]);

    // Apply high contrast
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Apply audio settings to global CSS variables
    root.style.setProperty('--audio-speed', settings.audioSpeed.toString());
    root.style.setProperty('--audio-volume', settings.audioVolume.toString());
  };

  const updateFontSize = (size: FontSize) => {
    setSettings(prev => ({ ...prev, fontSize: size }));
  };

  const toggleHighContrast = () => {
    setSettings(prev => ({ ...prev, highContrast: !prev.highContrast }));
  };

  const updateAudioSpeed = (speed: AudioSpeed) => {
    setSettings(prev => ({ ...prev, audioSpeed: speed }));
  };

  const updateAudioVolume = (volume: number) => {
    setSettings(prev => ({ ...prev, audioVolume: volume }));
  };

  const toggleAutoPlayAudio = () => {
    setSettings(prev => ({ ...prev, autoPlayAudio: !prev.autoPlayAudio }));
  };

  return (
    <AccessibilityContext.Provider
      value={{
        settings,
        updateFontSize,
        toggleHighContrast,
        updateAudioSpeed,
        updateAudioVolume,
        toggleAutoPlayAudio,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};
